import { useProjectId } from '@consigli/hooks';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { AgentsLoading } from '@/components/agents-loading';
import { useProjectServices } from '@/features/project-services/use-project-services';
import { CenteredLayout } from '@/layouts/centered-content';
import { FluidLayout } from '@/layouts/fluid-content';
import { Nav } from '@/layouts/nav';
import { Route } from '@/routes';

import { ServiceCardGrid } from './service-card-grid';

export const ServiceListPage: FC = () => {
  const projectId = useProjectId();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { services: filteredServices, isLoading, project } = useProjectServices();

  const hasActiveServices = filteredServices.filter((service) => service.active).length !== 0;
  // Reads as "has no active services" but means "has no *inactive* services";
  // consumed below as `!hasNoActiveServices` to gate the available section.
  const hasNoActiveServices = filteredServices.filter((service) => !service.active).length === 0;
  const onBackButtonClick = () => {
    navigate(`/${Route.PROJECTS}`);
  };

  return (
    <FluidLayout>
      <Nav handleClick={onBackButtonClick} />
      {isLoading ? (
        <AgentsLoading />
      ) : (
        <>
          <CenteredLayout>
            <div className="flex flex-col lg:flex-row w-full px-14 pt-8">
              <span className="text-[2rem] font-semibold capitalize text-day-neutral-dark">
                {project?.name}
              </span>
            </div>
            {hasActiveServices && (
              <div className="px-14 pb-8">
                <div className="text-base py-4 text-day-neutral-dark font-semibold">
                  {t('service-list.applied-skills')}
                </div>
                <ServiceCardGrid
                  projectId={projectId}
                  services={filteredServices.filter((service) => service.active)}
                />
              </div>
            )}
          </CenteredLayout>
          {!hasNoActiveServices && (
            <div className="bg-day-light-3 flex-1">
              <CenteredLayout>
                <div className="px-14 pt-8 pb-8">
                  <div className="text-base py-4 text-day-neutral-dark font-semibold">
                    {t('service-list.available-skills')}
                  </div>
                  <ServiceCardGrid
                    projectId={projectId}
                    services={filteredServices.filter((service) => !service.active)}
                  />
                </div>
              </CenteredLayout>
            </div>
          )}
        </>
      )}
    </FluidLayout>
  );
};
